import { portcoUpdatesApi } from "@/api/portco-updates-api";
import { workspaceApi } from "@/api/workspace-api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import type { DealTransformed } from "@/lib/schema/deal-schema";
import { isDevS3File } from "@/lib/url-utils";
import { getStageText } from "@/lib/utils";
import { useWorkspaceStore } from "@/store/workspace-store";
import { useQuery } from "@tanstack/react-query";
import {
  Award,
  Calendar,
  ChevronDown,
  Download,
  ExternalLink,
  File,
  FileArchive,
  FileImage,
  FileSpreadsheet,
  FileText,
  Globe,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Target,
  TrendingUp,
  Users2,
} from "lucide-react";
import { memo, type ReactNode, useState } from "react";
import { toast } from "sonner";

// Extension → icon. Colour is intentionally uniform (muted) so file rows
// sit on the same neutral surface as the rest of the tab.
const FILE_ICON_BY_EXT: Record<string, typeof File> = {
  pdf: FileText,
  doc: File,
  docx: File,
  ppt: FileText,
  pptx: FileText,
  jpg: FileImage,
  jpeg: FileImage,
  png: FileImage,
  ico: FileImage,
  zip: FileArchive,
  csv: FileSpreadsheet,
  xlsx: FileSpreadsheet,
};

function getExt(fileName: string) {
  return fileName.split(".").pop()?.toLowerCase() || "";
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function downloadFile(url: string, filename: string) {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Uppercase rule-divider section header — the "research terminal" convention
// used across the new deal-detail surfaces.
function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="mb-3 flex items-center gap-3">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/80">
        {children}
      </span>
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}

function SubLabel({ children }: { children: ReactNode }) {
  return (
    <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/80">
      {children}
    </div>
  );
}

function SnapshotField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/80">
        {label}
      </div>
      <div className="break-words text-sm font-medium text-foreground">
        {children}
      </div>
    </div>
  );
}

interface PortcoTabProps {
  currentDeal: DealTransformed;
}

function PortcoTab({ currentDeal }: PortcoTabProps) {
  const { activeWorkspaceId } = useWorkspaceStore();
  const companyId = currentDeal.id;
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  const { data, isLoading, isError } = useQuery({
    queryKey: ["portco-updates", activeWorkspaceId, companyId],
    enabled: !!activeWorkspaceId && !!companyId,
    queryFn: async () => {
      const opportunityId = activeWorkspaceId as string;
      const [workspaceRes, updatesRes] = await Promise.all([
        workspaceApi.getWorkspace(opportunityId),
        portcoUpdatesApi.getUpdates(opportunityId, companyId),
      ]);
      // A non-success envelope (HTTP 200 with status:"error") is a real
      // failure, not an empty timeline — surface it through the isError
      // branch instead of silently rendering the "no updates yet" state.
      if (updatesRes.status !== "success") {
        throw new Error("Failed to load portco updates");
      }
      return {
        // Workspace is secondary (invite link + portco_form labels) —
        // tolerate its absence so a workspace hiccup can't hide updates
        // that loaded fine.
        workspace:
          workspaceRes.status === "success" ? workspaceRes.result : null,
        updates: updatesRes.result ?? [],
      };
    },
  });

  const workspace = data?.workspace ?? null;
  const updates = data?.updates ?? [];

  const toggleItem = (id: string) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="flex h-64 flex-col items-center justify-center gap-1 text-center">
          <p className="text-sm font-medium text-foreground">
            Failed to load updates
          </p>
          <p className="text-sm text-muted-foreground">
            Please try again in a moment.
          </p>
        </div>
      </div>
    );
  }

  const companyName =
    updates[0]?.company_snapshot.company_name ||
    currentDeal.overview.name ||
    "Portfolio Company";

  const header = (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 flex-1">
        <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
          Growth Timeline
        </h2>
        <p className="mt-1 text-sm text-muted-foreground sm:text-base">
          Track {companyName}'s progress and milestones over time
        </p>
      </div>
      <Badge
        variant="outline"
        className="flex w-fit items-center gap-1.5 font-mono text-xs tabular-nums"
      >
        <TrendingUp className="h-3.5 w-3.5" />
        {updates.length} {updates.length === 1 ? "Update" : "Updates"}
      </Badge>
    </div>
  );

  if (updates.length === 0) {
    const submissionUrl = workspace?.portal_slug
      ? `${window.location.origin}/p/${workspace.portal_slug}/submission`
      : `${window.location.origin}/portal/submission?id=${activeWorkspaceId}`;

    return (
      <div className="mx-auto max-w-4xl p-6">
        {header}
        <div className="rounded-lg border border-border bg-card p-8 text-center sm:p-12">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
            <Calendar className="h-7 w-7 text-muted-foreground" />
          </div>
          <h3 className="text-sm font-semibold text-foreground">
            No updates yet
          </h3>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            We haven't received any portfolio updates yet. Invite the company to
            share their progress and milestones.
          </p>

          <div className="mx-auto mt-6 max-w-lg rounded-lg border border-border bg-background p-4 text-left">
            <SubLabel>Invite to provide updates</SubLabel>
            <p className="mt-1.5 text-xs text-muted-foreground">
              Share this link so the company can submit updates about their
              progress, milestones, and developments.
            </p>
            <div className="mt-3 rounded-md border border-border bg-muted/40 p-2.5">
              <code className="break-all font-mono text-xs text-foreground">
                {submissionUrl}
              </code>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 w-full"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(submissionUrl);
                  toast.success("Link copied to clipboard!");
                } catch {
                  toast.error("Failed to copy link to clipboard");
                }
              }}
            >
              Copy Link
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const snapshot = updates[0].company_snapshot;

  return (
    <div className="mx-auto max-w-4xl p-6">
      {header}

      {/* Current status */}
      <section className="mb-8">
        <SectionLabel>Current Status</SectionLabel>
        <div className="rounded-lg border border-border bg-card p-4 sm:p-5">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <Globe className="h-4 w-4 text-muted-foreground" />
                Company Details
              </div>
              <dl className="space-y-2 text-sm">
                {snapshot.company_website && (
                  <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
                    <dt className="shrink-0 text-muted-foreground">Website:</dt>
                    <dd className="min-w-0">
                      <a
                        href={snapshot.company_website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 break-all text-primary hover:underline"
                      >
                        {snapshot.company_website}
                        <ExternalLink className="h-3 w-3 shrink-0" />
                      </a>
                    </dd>
                  </div>
                )}
                {snapshot.address && (
                  <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
                    <dt className="shrink-0 text-muted-foreground">Location:</dt>
                    <dd className="break-words text-foreground">
                      {snapshot.address}
                    </dd>
                  </div>
                )}
                {snapshot.industry && snapshot.industry.length > 0 && (
                  <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
                    <dt className="shrink-0 text-muted-foreground">Industry:</dt>
                    <dd className="break-words text-foreground">
                      {snapshot.industry.join(", ")}
                    </dd>
                  </div>
                )}
                {snapshot.business_model && (
                  <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
                    <dt className="shrink-0 text-muted-foreground">
                      Business Model:
                    </dt>
                    <dd className="break-words text-foreground">
                      {snapshot.business_model}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <Users2 className="h-4 w-4 text-muted-foreground" />
                Contact Information
              </div>
              <div className="space-y-2 text-sm">
                {snapshot.contact_email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="break-all text-foreground">
                      {snapshot.contact_email}
                    </span>
                  </div>
                )}
                {snapshot.contact_phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="font-mono tabular-nums text-foreground">
                      {snapshot.contact_phone}
                    </span>
                  </div>
                )}
                {snapshot.address && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="break-words text-foreground">
                      {snapshot.address}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Metrics */}
          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-md border border-border bg-background p-3">
              <div className="mb-1.5 flex items-center gap-1.5">
                <Target className="h-3.5 w-3.5 text-muted-foreground" />
                <SubLabel>Total Updates</SubLabel>
              </div>
              <div className="font-mono text-lg font-semibold tabular-nums text-foreground">
                {updates.length}
              </div>
            </div>
            <div className="rounded-md border border-border bg-background p-3">
              <div className="mb-1.5 flex items-center gap-1.5">
                <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
                <SubLabel>Latest Update</SubLabel>
              </div>
              <div className="font-mono text-sm font-semibold tabular-nums text-foreground">
                {formatDate(updates[0].created_on)}
              </div>
              <div className="mt-0.5 truncate text-xs text-muted-foreground">
                {updates[0].update_title}
              </div>
            </div>
            <div className="rounded-md border border-border bg-background p-3">
              <div className="mb-1.5 flex items-center gap-1.5">
                <Award className="h-3.5 w-3.5 text-muted-foreground" />
                <SubLabel>Company Stage</SubLabel>
              </div>
              <div className="text-sm font-semibold text-foreground">
                {snapshot.stage ? getStageText(snapshot.stage) : "Not specified"}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section>
        <SectionLabel>Growth Timeline</SectionLabel>
        <div className="relative">
          {/* Connector track behind the dots (only when there's more than one). */}
          {updates.length > 1 && (
            <div
              className="absolute bottom-3 left-[11px] top-3 w-px bg-border"
              aria-hidden
            />
          )}
          <div className="space-y-5">
            {updates.map((update) => {
              const isOpen = openItems.has(update._id);
              const visibleFiles = (update.files ?? []).filter(
                (f) => !isDevS3File(f.file_url)
              );

              return (
                <div key={update._id} className="relative flex gap-4">
                  <div className="relative z-10 mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-card ring-1 ring-border">
                    <Calendar className="h-3 w-3 text-muted-foreground" />
                  </div>

                  <Collapsible
                    open={isOpen}
                    onOpenChange={() => toggleItem(update._id)}
                    className="min-w-0 flex-1"
                  >
                    <CollapsibleTrigger asChild>
                      <button
                        type="button"
                        className="group flex w-full items-start justify-between gap-3 text-left"
                      >
                        <div className="min-w-0">
                          <h4 className="truncate text-sm font-semibold text-foreground">
                            {update.update_title}
                          </h4>
                          <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">
                            {update.update_description}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-2 pt-0.5">
                          <span className="font-mono text-xs tabular-nums text-muted-foreground">
                            {formatDate(update.created_on)}
                          </span>
                          <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
                        </div>
                      </button>
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <div className="mt-3 space-y-5 rounded-lg border border-border bg-background p-4">
                        {/* Update details */}
                        <section className="space-y-2">
                          <SubLabel>Update Details</SubLabel>
                          <p className="text-sm leading-relaxed text-foreground">
                            {update.update_description}
                          </p>
                        </section>

                        {/* Company snapshot */}
                        <section className="space-y-2">
                          <SubLabel>Company Snapshot</SubLabel>
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            <SnapshotField label="Company Name">
                              {update.company_snapshot.company_name}
                            </SnapshotField>
                            {update.company_snapshot.company_website && (
                              <SnapshotField label="Website">
                                <a
                                  href={update.company_snapshot.company_website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 break-all text-primary hover:underline"
                                >
                                  {update.company_snapshot.company_website}
                                  <ExternalLink className="h-3 w-3 shrink-0" />
                                </a>
                              </SnapshotField>
                            )}
                            {update.company_snapshot.industry &&
                              update.company_snapshot.industry.length > 0 && (
                                <SnapshotField label="Industry">
                                  {update.company_snapshot.industry.join(", ")}
                                </SnapshotField>
                              )}
                            {update.company_snapshot.business_model && (
                              <SnapshotField label="Business Model">
                                {update.company_snapshot.business_model}
                              </SnapshotField>
                            )}
                            {update.company_snapshot.stage && (
                              <SnapshotField label="Stage">
                                {getStageText(update.company_snapshot.stage)}
                              </SnapshotField>
                            )}
                            {update.company_snapshot.contact_email && (
                              <SnapshotField label="Contact Email">
                                {update.company_snapshot.contact_email}
                              </SnapshotField>
                            )}
                          </div>
                          {update.company_snapshot.short_bio && (
                            <SnapshotField label="Company Description">
                              {update.company_snapshot.short_bio}
                            </SnapshotField>
                          )}
                        </section>

                        {/* Portfolio update answers */}
                        {update.portco_update &&
                          Object.keys(update.portco_update).length > 0 &&
                          workspace?.portco_form && (
                            <section className="space-y-2">
                              <SubLabel>Portfolio Update Details</SubLabel>
                              <div className="space-y-2">
                                {Object.entries(update.portco_update).map(
                                  ([questionKey, answer]) => {
                                    const formField =
                                      workspace.portco_form[questionKey];
                                    return (
                                      <div
                                        key={questionKey}
                                        className="rounded-md border border-border bg-card p-3"
                                      >
                                        <div className="text-sm font-medium text-foreground">
                                          {formField?.title || questionKey}
                                        </div>
                                        {formField?.helper_text && (
                                          <div className="mt-0.5 text-xs italic text-muted-foreground">
                                            {formField.helper_text}
                                          </div>
                                        )}
                                        <div className="mt-1 text-sm text-foreground">
                                          {answer}
                                        </div>
                                      </div>
                                    );
                                  }
                                )}
                              </div>
                            </section>
                          )}

                        {/* Attachments */}
                        {visibleFiles.length > 0 && (
                          <section className="space-y-2">
                            <SubLabel>
                              Attached Files ({visibleFiles.length})
                            </SubLabel>
                            <div className="space-y-2">
                              {visibleFiles.map((file, index) => {
                                const Icon =
                                  FILE_ICON_BY_EXT[getExt(file.file_name)] ??
                                  File;
                                return (
                                  <div
                                    key={`${file.file_name}-${file.file_size}-${index}`}
                                    className="flex items-center justify-between gap-3 rounded-md border border-border bg-card p-3"
                                  >
                                    <div className="flex min-w-0 flex-1 items-center gap-3">
                                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted">
                                        <Icon className="h-4 w-4 text-muted-foreground" />
                                      </div>
                                      <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                          <span
                                            className="truncate text-sm font-medium text-foreground"
                                            title={file.file_name}
                                          >
                                            {file.file_name}
                                          </span>
                                          <Badge
                                            variant="outline"
                                            className="shrink-0 text-[10px] uppercase tracking-wide"
                                          >
                                            {file.file_type}
                                          </Badge>
                                        </div>
                                        <div className="font-mono text-xs tabular-nums text-muted-foreground">
                                          {file.file_size}
                                        </div>
                                      </div>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 shrink-0"
                                      onClick={() =>
                                        downloadFile(
                                          file.file_url,
                                          file.file_name
                                        )
                                      }
                                    >
                                      <Download className="h-4 w-4" />
                                    </Button>
                                  </div>
                                );
                              })}
                            </div>
                          </section>
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}

export default memo(PortcoTab);
